import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import SEO from "components/SEO/SEO";

export default function PrivacyPolicy() {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(["personal-information"])
    );

    const toggleSection = (section: string) => {
        const newSections = new Set(expandedSections);
        if (newSections.has(section)) {
            newSections.delete(section);
        } else {
            newSections.add(section);
        }
        setExpandedSections(newSections);
    };

    const isExpanded = (section: string) => expandedSections.has(section);

    return (
        <div className="container transition-colors duration-200">
            <SEO
              title="Privacy Policy"
              description="Read the Nomadroof privacy policy to understand how we collect, use and protect your personal data."
              canonical="/policy"
              noIndex={true}
            />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Privacy Policy
                    </h1>
                </div>

                {/* Introduction */}
                <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover mb-4">
                    <p className="text-gray-600 dark:text-gray-300">
                        This Privacy Policy governs the manner in which Nomadroof collects, uses, maintains and discloses information collected from users (each, a "User") of the{" "}
                        <a
                            href="http://www.nomadroof.com/"
                            aria-label="Nomadroof website"
                            className="text-orange-500 hover:text-orange-600 dark:hover:text-orange-400 underline transition-colors"
                        >
                            Nomadroof website
                        </a>{" "}
                        ("Site"). This privacy policy applies to the Site and all products and services offered by Nomadroof.
                    </p>
                </div>

                {/* Main Content */}
                <div className="space-y-4">
                    {policySections.map((section) => {
                        const expanded = isExpanded(section.id);
                        return (
                            <div
                                key={section.id}
                                className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover ]"
                            >
                                <button
                                    className={`w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${expanded ? "rounded-t-3xl" : "rounded-3xl"
                                        }`}
                                    onClick={() => toggleSection(section.id)}
                                    aria-expanded={expanded}
                                    aria-controls={`${section.id}-content`}
                                >
                                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                                        {section.title}
                                    </h2>
                                    {expanded ? (
                                        <ChevronUpIcon className="h-5 w-5 text-orange-500" />
                                    ) : (
                                        <ChevronDownIcon className="h-5 w-5 text-orange-500" />
                                    )}
                                </button>

                                {expanded && (
                                    <div
                                        id={`${section.id}-content`}
                                        className="px-6 pb-6 pt-2 border-t text-start border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm leading-relaxed"
                                    >
                                        {section.content}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {/* Data Retention Table */}
                    <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                        <button
                            className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                            onClick={() => toggleSection("data-retention")}
                            aria-expanded={isExpanded("data-retention")}
                            aria-controls="data-retention-content"
                        >
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                                Data Retention Periods
                            </h2>
                            {isExpanded("data-retention") ? (
                                <ChevronUpIcon className="h-5 w-5 text-orange-500" />
                            ) : (
                                <ChevronDownIcon className="h-5 w-5 text-orange-500" />
                            )}
                        </button>

                        {isExpanded("data-retention") && (
                            <div id="data-retention-content" className="px-6 pb-6 pt-2 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                                    Nomadroof has assessed the types of personal data that Nomadroof holds and the purposes the Company use it for. The table below sets out the retention periods that the Company has set for the different departments within the Company, and the different types of data that they each hold.
                                </p>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                                        <thead className="bg-gray-50 dark:bg-gray-700">
                                            <tr>
                                                {tableHeaders.map((header) => (
                                                    <th
                                                        key={header}
                                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {dataRetentionRows.map((row, index) => (
                                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                    {row.map((cell, cellIndex) => (
                                                        <td
                                                            key={cellIndex}
                                                            className="px-4 py-3 whitespace-normal text-gray-600 dark:text-gray-300 text-sm"
                                                        >
                                                            {cell}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Data Rights Section */}
                    <div className="nc-CardAuthorBox relative flex flex-col px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Your Data Rights</h2>
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">When can I request data deletion?</h3>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    You can request a data deletion at any time from within the app. Please note that this request is irreversible – once you have requested for your data to be deleted you will not be able to access your account again.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">How do I request data deletion?</h3>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    Please contact our Customer Support team via the "Contact Us" page on our website, and we'll help you make your deletion request. You will need to provide enough information for us to verify your identity and the specific data you want to have deleted.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Policy Updates */}
                    <div className="nc-CardAuthorBox relative flex flex-col px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Policy Updates</h2>
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Changes to this policy</h3>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    Nomadroof has the discretion to update this privacy policy at any time. When we do, we will revise the updated date at the bottom of this page. We encourage Users to frequently check this page for any changes to stay informed about how we are helping to protect the personal information we collect.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Your acceptance</h3>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    By using this Site and/or Application, you signify your acceptance of this policy. If you do not agree to this policy, please do not use our Site. Your continued use of the Site following the posting of changes to this policy will be deemed your acceptance of those changes.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="nc-CardAuthorBox relative flex flex-col px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover mt-5">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Contact Us</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                        If you have any questions about this Privacy Policy, please contact us at{" "}
                        <a href="mailto:privacy@nomadroof.com" className="text-orange-500 hover:underline">
                            privacy@nomadroof.com
                        </a>.
                    </p>
                </div>
            </div>
        </div>
    );
}

// Data for policy sections
const policySections = [
    {
        id: "personal-information",
        title: "Personal Information Collection",
        content: (
            <p>
                We may collect personal identification information from Users in a variety of ways in connection with activities, services, features or resources we make available on our Site. Users may visit our Site anonymously. We will collect personal identification information from Users only if they voluntarily submit such information to us. Users can always refuse to supply personally identification information, except that it may prevent them from engaging in certain Site related activities.
            </p>
        )
    },
    {
        id: "non-personal-information",
        title: "Non-personal Identification Information",
        content: (
            <p>
                We may collect non-personal identification information about Users whenever they interact with our Site. Non-personal identification information may include the browser name, the type of computer and technical information about Users means of connection to our Site, such as the operating system and the Internet service providers utilized and other similar information.
            </p>
        )
    },
    {
        id: "browser-cookies",
        title: "Web Browser Cookies",
        content: (
            <p>
                Our Site use "cookies" to enhance User experience. User's web browser places cookies on their hard drive for record-keeping purposes and sometimes to track information about them. User may choose to set their web browser to refuse cookies, or to alert you when cookies are being sent. If they do so, note that some parts of the Site may not function properly.
            </p>
        )
    },
    {
        id: "your-information",
        title: "How We Protect Your Information",
        content: (
            <p>
                We adopt appropriate data collection, storage and processing practices and security measures to protect against unauthorized access, alteration, disclosure or destruction of your personal information, username, password, transaction information and data stored on our Site.
            </p>
        )
    },
    {
        id: "your-personal-information",
        title: "Your Personal Information",
        content: (
            <>
                <p className="mb-3">
                    The information we collect may be use when legally required to do so at the request of governmental authorities conducting an investigation to verify or enforce compliance with the policies governing our Site and Application and applicable laws or to protect against misuse or unauthorized use of our Site and Application to a successor entity in connection with a corporate merger consolidation sale of assets or other corporate change respecting the Site and Application.
                </p>
                <p className="mb-3">
                    Persons who supply us with their telephone numbers on-line will only receive telephone contact from us with information regarding orders they have placed on-line and verification of account and/or accommodation listing. Please provide us with your name and phone number.
                </p>
                <p>
                    With respect to Ad Servers. We do not partner with or have special relationships with any ad server companies.
                </p>
            </>
        )
    },
    {
        id: "retention-policy",
        title: "Nomadroof Limited: Retention Policy",
        content: (
            <>
                <p className="mb-3">
                    1.1 Nomadroof Limited (the "Company") must, in respect of its processing of personal data, comply with the Data Protection Act 2018, the General Data Protection Regulation 2016/679, and related legislation (together, "Data Protection Laws").
                </p>
                <p className="mb-3">
                    1.2 This Retention Policy should be read in conjunction with the Company's Data Protection Policy, which sets out the Company's overall approach to data protection matters and sets out the rationale for why a Retention Policy is required for personal data.
                </p>
                <p className="mb-3">
                    1.3 Nomadroof is under a legal obligation only to keep personal data for as long as the Company needs it. Once the Company no longer needs personal data, the Company must securely delete it. The Company recognizes that the correct and lawful treatment of data will maintain confidence in the Company and will provide for a successful working environment.
                </p>
                <p className="mb-3">
                    1.4 This Policy applies to all Company employees, consultants, contractors and temporary personnel hired to work on behalf of the Company ("Company Personnel").
                </p>
                <p className="mb-3">
                    1.5 All Company Personnel with access to personal data must comply with this Retention Policy at all times.
                </p>
                <p className="mb-3">
                    1.6 Please read this Retention Policy carefully. If you have any queries regarding this Retention Policy, please consult your manager and/ or the Data Protection Representative. You are advised that any breach of this Retention Policy will be treated seriously and may result in disciplinary action being taken against you.
                </p>
                <p>
                    1.7 Company Personnel will receive a copy of this Policy when they start and may receive periodic revisions of this Policy. This Policy does not form part of any Company Personnel's contract of employment and the Company reserves the right to change this Policy at any time. All Company Personnel are obliged to comply with this Policy at all times.
                </p>
            </>
        )
    },
    {
        id: "about-policy",
        title: "About This Policy",
        content: (
            <p>
                This Retention Policy explains how the Company complies with our legal obligation not to keep personal data for longer than we need it and sets out when different types of personal data will be deleted. In particular, it sets out details of the Company's policies for the retention of Special Category personal data.
            </p>
        )
    },
    {
        id: "data-subject-rights",
        title: "Your Data Subject Rights",
        content: (
            <p>
                Under applicable data protection laws, you have the right to access, rectify, port, and erase your data. You also have the right to object to and restrict certain processing of your data. This includes the right to object to our processing of your data for direct marketing.
            </p>
        )
    }
];

// Table data
const tableHeaders = [
    "Data Type",
    "Delete If Manual",
    "Delete If Electronic",
    "Deletion Method (Manual)",
    "Deletion Method (Electronic)"
];

const dataRetentionRows = [
    [
        "Employee records",
        "5 years (please check Nomadroof Employee Privacy Statement)",
        "5 years (please check Nomadroof Employee Privacy Statement)",
        "Documentation shredding",
        "Delete records wherever they're stored"
    ],
    [
        "Customer (Tenant) details",
        "2 years",
        "2 years",
        "Documentation shredding",
        "In an automated manner via a job that runs every day"
    ],
    [
        "Customer (Host) details",
        "2 years",
        "2 years",
        "Documentation shredding",
        "In an automated manner via a job that runs every day"
    ],
    [
        "Supplier details",
        "2 years",
        "2 years",
        "Documentation shredding",
        "In an automated manner via a job that runs every day"
    ]
];